<template>
  <modal @close="$emit('close')" class-name="shipping-plan-modal-container">
    <template slot="header">
      <h4 class="modal-title">{{ $t("products.createProduct") }}</h4>
      <md-button class="md-simple md-just-icon md-round modal-default-button" @click="$emit('close')">
        <md-icon>clear</md-icon>
      </md-button>
    </template>

    <template slot="body">
      <ValidationObserver v-slot="{}" ref="createForm">
        <md-tabs>
          <md-tab id="ProductGeneral" :md-label="$t('products.ProductGeneral')">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-40">
                <ValidationProvider name="sku" rules="required" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.sku") }}</label>
                    <md-input v-model="sku" type="text"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-30">
                <ValidationProvider name="gtin" :rules="{ regex: /^[0-9]{8,14}$/ }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.gtin") }}</label>
                    <md-input v-model="gtin" type="text" inputmode="numeric" maxlength="14"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-30">
                <md-field>
                  <label>{{ $t("products.condition") }}</label>
                  <md-select v-model="condition" name="condition" id="createProductCondition">
                    <md-option value="Neu">{{ $t("products.conditionNew") }}</md-option>
                    <md-option value="B-Ware">{{ $t("products.conditionBWare") }}</md-option>
                  </md-select>
                </md-field>
              </div>
            </div>
            <ValidationProvider name="name" rules="required" v-slot="{ passed, failed, errors }">
              <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                <label>{{ $t("products.name") }}</label>
                <md-input v-model="name" type="text"></md-input>
                <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
              </md-field>
            </ValidationProvider>

            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>{{ $t("products.shippingSizeLength") }}</label>
                  <md-input v-model="shippingSizeLength" type="number"></md-input>
                  <span class="md-suffix">cm</span>
                </md-field>
              </div>
              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>{{ $t("products.shippingSizeWidth") }}</label>
                  <md-input v-model="shippingSizeWidth" type="number"></md-input>
                  <span class="md-suffix">cm</span>
                </md-field>
              </div>
              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>{{ $t("products.shippingSizeHeight") }}</label>
                  <md-input v-model="shippingSizeHeight" type="number"></md-input>
                  <span class="md-suffix">cm</span>
                </md-field>
              </div>
              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>{{ $t("products.shippingSizeWeight") }}</label>
                  <md-input v-model="shippingSizeWeight" type="number"></md-input>
                  <span class="md-suffix">kg</span>
                </md-field>
              </div>
            </div>

            <div style="width: 30%">
              <md-switch v-model="bulky" value="true" class="md-primary">{{ $t("products.bulky") }}</md-switch>
            </div>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50">
                <ValidationProvider name="hsUK" rules="digits:8" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.hsUK") }}</label>
                    <md-input v-model="hsUK" type="number" :disabled="isBWare"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
                <ValidationProvider name="hsEU" rules="digits:10" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.hsEU") }}</label>
                    <md-input v-model="hsEU" type="number" :disabled="isBWare"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
                <ValidationProvider name="hsCH" rules="integer" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.hsCH") }}</label>
                    <md-input v-model="hsCH" type="number" :disabled="isBWare"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
                <ValidationProvider name="customsTariff" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.customsTariff") }}</label>
                    <md-input v-model="customsTariff" type="number" min="0.0" step="0.1" inputmode="decimal" :disabled="isBWare"></md-input>
                    <span class="md-suffix">%</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50">
                <ValidationProvider name="outboundCarrier" rules="required" v-slot="{ errors }">
                  <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                    <label v-if="!!outboundCarrierName">{{ $t("products.outboundCarrier") }}</label>
                    <SelectCarrier v-model="outboundCarrierName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.outboundCarrier')" :multiple="false" :customClass="'custom-select'" />
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </div>
                </ValidationProvider>
                <ValidationProvider name="carrierUK" rules="required" v-slot="{ errors }">
                  <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                    <label v-if="!!carrierUKName">{{ $t("products.carrierUK") }}</label>
                    <SelectCarrier v-model="carrierUKName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.carrierUK')" :multiple="false" :customClass="'custom-select'" />
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </div>
                </ValidationProvider>
                <ValidationProvider name="carrierEU" rules="required" v-slot="{ errors }">
                  <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                    <label v-if="!!carrierEUName">{{ $t("products.carrierEU") }}</label>
                    <SelectCarrier v-model="carrierEUName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.carrierEU')" :multiple="false" :customClass="'custom-select'" />
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </div>
                </ValidationProvider>
              </div>
            </div>

            <ValidationProvider name="supplierId" rules="required" v-slot="{ errors }">
              <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-top: 15px">
                <label v-if="supplierIds.length">{{ $t("products.supplier") }}</label>
                <SelectSupplier v-model="supplierIds" multiple :clearable="false" :placeholder="$t('products.supplier')" custom-class="custom-select" />
                <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                <div v-if="!errors[0]" class="supplier-hint">{{ $t("products.supplierHint") }}</div>
              </div>
            </ValidationProvider>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50">
                <ValidationProvider name="moq" rules="integer|min_value:0" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.moq") }}</label>
                    <md-input v-model="moq" type="number" min="0" step="1"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50" style="text-align: left">
                <md-switch v-model="eol" class="md-primary">{{ $t("products.eol") }}</md-switch>
              </div>
            </div>
          </md-tab>
          <md-tab id="ProductDetails" :md-label="$t('products.ProductDetails')">
            <ValidationProvider name="cabQuantity" rules="integer|min_value:0" v-slot="{ passed, failed, errors }">
              <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                <label>{{ $t("products.cabQuantity") }}</label>
                <md-input v-model="cabQuantity" type="number" inputmode="numeric"></md-input>
                <span class="md-suffix">pcs</span>
                <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
              </md-field>
            </ValidationProvider>

            <md-subheader>{{ $t("products.masterboxDementions") }}</md-subheader>
            <div class="md-layout md-gutter mb-3">
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="skuMasterbox" :rules="{ regex: /^[a-zA-Z0-9_-]+$/ }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.skuMasterbox") }}</label>
                    <md-input v-model="skuMasterbox" type="text"></md-input>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="mbSkuAmount" rules="min_value:0|integer" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.mbSkuAmount") }}</label>
                    <md-input v-model="mbSkuAmount" type="number" min="0"></md-input>
                    <span class="md-suffix">pcs</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="mbSkuLength" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.mbSkuLength") }}</label>
                    <md-input v-model="mbSkuLength" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">cm</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="mbSkuWidth" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.mbSkuWidth") }}</label>
                    <md-input v-model="mbSkuWidth" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">cm</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="mbSkuHeight" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.mbSkuHeight") }}</label>
                    <md-input v-model="mbSkuHeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">cm</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="mbSkuWeight" :rules="{ min_value: 0.1, decimal: 2 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.mbSkuWeight") }}</label>
                    <md-input v-model="mbSkuWeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">kg</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
            </div>
            <md-divider></md-divider>
            <md-subheader>{{ $t("products.shippingWeights") }}</md-subheader>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="skuPaperWeight" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.skuPaperWeight") }}</label>
                    <md-input v-model="skuPaperWeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">gr</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="skuPlasticWeight" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.skuPlasticWeight") }}</label>
                    <md-input v-model="skuPlasticWeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">gr</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="skuWoodWeight" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.skuWoodWeight") }}</label>
                    <md-input v-model="skuWoodWeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">gr</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout-item md-size-50 mb-3">
                <ValidationProvider name="skuMetalWeight" :rules="{ min_value: 0.1, decimal: 1 }" v-slot="{ passed, failed, errors }">
                  <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                    <label>{{ $t("products.skuMetalWeight") }}</label>
                    <md-input v-model="skuMetalWeight" type="number" min="0.1" step="0.1" inputmode="decimal"></md-input>
                    <span class="md-suffix">gr</span>
                    <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                  </md-field>
                </ValidationProvider>
              </div>
            </div>
          </md-tab>
        </md-tabs>
      </ValidationObserver>
    </template>

    <template slot="footer">
      <md-button class="md-secondary mr-1" @click="$emit('close')">{{ $t("products.close") }}</md-button>
      <md-button class="md-success" @click="save">{{ $t("products.save") }}</md-button>
    </template>
  </modal>
</template>

<script>
import { Modal } from "@/components";
import SelectCarrier from "../../components/SelectCarrier/SelectCarrier.vue";
import SelectSupplier from "../../components/SelectSupplier/SelectSupplier.vue";

export default {
  components: { Modal, SelectCarrier, SelectSupplier },
  emits: ["close", "saved"],
  data() {
    return {
      sku: null,
      gtin: null,
      name: null,
      shippingSizeLength: null,
      shippingSizeWidth: null,
      shippingSizeHeight: null,
      shippingSizeWeight: null,
      condition: "Neu",
      hsUK: null,
      hsEU: null,
      hsCH: null,
      customsTariff: null,
      outboundCarrier: null,
      carrierUK: null,
      carrierEU: null,
      bulky: "",
      supplierIds: [],
      moq: null,
      eol: false,
      cabQuantity: null,
      mbSkuAmount: null,
      mbSkuHeight: null,
      mbSkuLength: null,
      mbSkuWeight: null,
      mbSkuWidth: null,
      skuMasterbox: null,
      skuMetalWeight: null,
      skuPaperWeight: null,
      skuPlasticWeight: null,
      skuWoodWeight: null,
    };
  },
  computed: {
    isBWare() {
      return this.condition === "B-Ware";
    },
    carrierOptions() {
      return (this.$store.state.shippingData.carriers || []).filter((c) => this.allowedCarriers.includes(c.name));
    },
    allowedCarriers() {
      return ["DHL", "DPD", "Spedition"];
    },
    outboundCarrierName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.outboundCarrier);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.outboundCarrier = c ? c.id : null;
      },
    },
    carrierUKName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.carrierUK);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.carrierUK = c ? c.id : null;
      },
    },
    carrierEUName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.carrierEU);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.carrierEU = c ? c.id : null;
      },
    },
  },
  created() {
    if (!this.$store.state.shippingData.carriers || !this.$store.state.shippingData.carriers.length) {
      this.$store.dispatch("shippingData/getCarriers");
    }
    if (!this.$store.state.shippingData.supplierNames || !this.$store.state.shippingData.supplierNames.length) {
      this.$store.dispatch("shippingData/getSupplierNames");
    }
  },
  methods: {
    save() {
      this.$refs.createForm.validate().then((success) => {
        if (!success) {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.messages.checkFields"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
          return;
        }
        const carrier = this.carrierOptions.find((i) => i.id === this.outboundCarrier);
        const carrierUK = this.carrierOptions.find((i) => i.id === this.carrierUK);
        const carrierEU = this.carrierOptions.find((i) => i.id === this.carrierEU);
        const payload = {
          sku: this.sku,
          gtin: this.gtin || null,
          name: this.name,
          shippingSizeLength: this.shippingSizeLength ? parseFloat(this.shippingSizeLength) : 0,
          shippingSizeWidth: this.shippingSizeWidth ? parseFloat(this.shippingSizeWidth) : 0,
          shippingSizeHeight: this.shippingSizeHeight ? parseFloat(this.shippingSizeHeight) : 0,
          shippingSizeWeight: this.shippingSizeWeight ? parseFloat(this.shippingSizeWeight) : 0,
          condition: this.condition || null,
          hsUK: this.hsUK,
          hsEU: this.hsEU,
          hsCH: this.hsCH,
          customsTariff: this.customsTariff === "" || this.customsTariff === null || this.customsTariff === undefined ? null : parseFloat(this.customsTariff),
          outboundCarrier: carrier && carrier.name,
          carrierUK: carrierUK && carrierUK.name,
          carrierEU: carrierEU && carrierEU.name,
          bulky: this.bulky ? this.bulky : "false",
          supplierIds: this.supplierIds,
          moq: this.moq === "" || this.moq === null || this.moq === undefined ? null : parseInt(this.moq, 10),
          eol: !!this.eol,
          cabQuantity: this.cabQuantity,
          mbSkuAmount: this.mbSkuAmount,
          mbSkuHeight: this.mbSkuHeight,
          mbSkuLength: this.mbSkuLength,
          mbSkuWeight: this.mbSkuWeight,
          mbSkuWidth: this.mbSkuWidth,
          skuMasterbox: this.skuMasterbox,
          skuMetalWeight: this.skuMetalWeight,
          skuPaperWeight: this.skuPaperWeight,
          skuPlasticWeight: this.skuPlasticWeight,
          skuWoodWeight: this.skuWoodWeight,
        };
        this.$store
          .dispatch("shippingData/createProduct", payload)
          .then(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("products.messages.created"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success",
            });
            this.$emit("saved", this.sku);
          })
          .catch(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("products.messages.productSkuDuplicated"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error",
            });
          });
      });
    },
  },
};
</script>
